import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import primaryLogo from '../assets/logo/syncehs-lockup-black-v2.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';

type Status = 'verifying' | 'ready' | 'invalid' | 'submitting' | 'success';

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMsg, setErrorMsg] = useState('');
  const [pendingTokenHash, setPendingTokenHash] = useState<string | null>(null);
  const [welcomeMode, setWelcomeMode] = useState(
    () => sessionStorage.getItem('syncehs-welcome-mode') === 'true'
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Verify the reset link on mount.
  useEffect(() => {
    let cancelled = false;

    const tokenHash = searchParams.get('token_hash');
    const type = searchParams.get('type');
    const code = searchParams.get('code');
    // Capture the welcome flag before the URL strip below wipes the query
    // string, and persist it so the app-level remount keeps welcome copy.
    const welcome = searchParams.get('welcome') === 'true';
    if (welcome) {
      setWelcomeMode(true);
      sessionStorage.setItem('syncehs-welcome-mode', 'true');
    }
    const isWelcome = welcome || welcomeMode;

    const run = async () => {
      // Preferred path: token_hash from the new email template.
      // verifyOtp is stateless — no PKCE verifier required, so cross-browser works.
      // Do NOT verify on load: email scanners (e.g. Outlook Safe Links) prefetch the
      // URL and would consume the single-use token. Hold it until form submit —
      // scanners follow links but don't submit forms.
      if (tokenHash && type === 'recovery') {
        setPendingTokenHash(tokenHash);
        // Strip the token from the URL so a refresh doesn't re-expose it.
        window.history.replaceState({}, '', '/reset-password');
        setStatus('ready');
        return;
      }

      // Legacy path: in-flight PKCE links emitted before the template change.
      // detectSessionInUrl auto-runs exchangeCodeForSession. It only succeeds if the
      // user is in the same browser that requested the reset (verifier present).
      if (code) {
        setTimeout(async () => {
          if (cancelled) return;
          const { data } = await supabase.auth.getSession();
          if (data.session) {
            window.history.replaceState({}, '', '/reset-password');
            setStatus('ready');
          } else {
            setErrorMsg(isWelcome
              ? 'Your setup link is invalid or has expired. Please request a new one below.'
              : 'Your reset link is invalid or has expired. Please request a new one.');
            setStatus('invalid');
          }
        }, 3000);
        return;
      }

      // No recognized params.
      // Post-remount recovery: if verifyOtp already established a session before an
      // app-level remount stripped the URL, show the form — submit will take the
      // plain updateUser path against the live session.
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      if (sessionData.session) {
        setStatus('ready');
        return;
      }

      setErrorMsg(isWelcome
        ? 'Your setup link is invalid or has expired. Please request a new one below.'
        : 'Your reset link is invalid or has expired. Please request a new one.');
      setStatus('invalid');
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Safety net for the legacy PKCE path: if exchangeCodeForSession succeeds before
  // our 3s poll, this fires first and flips status to 'ready'.
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setStatus((prev) => (prev === 'verifying' ? 'ready' : prev));
      }
    });
    return () => authListener.subscription.unsubscribe();
  }, []);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    // Deferred verification for the token_hash path: consume the single-use token
    // only now, on a human-initiated submit. Null on the legacy PKCE path.
    if (pendingTokenHash) {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: pendingTokenHash,
        type: 'recovery',
      });
      if (verifyError) {
        setErrorMsg(welcomeMode
          ? 'Your setup link is invalid or has expired. Please request a new one below.'
          : 'Your reset link is invalid or has expired. Please request a new one below.');
        setStatus('invalid');
        return;
      }
      setPendingTokenHash(null);
    }

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setErrorMsg(error.message);
      setStatus('ready');
      return;
    }

    // Hard redirect: survives the app-level remount that destroys this component
    // when verifyOtp's SIGNED_IN event flips the global loading gate.
    sessionStorage.removeItem('syncehs-welcome-mode');
    await supabase.auth.signOut();
    window.location.assign('/login');
  };

  return (
    <div className="min-h-screen bg-surface-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img src={primaryLogo} alt="SyncEHS" className="mx-auto h-auto w-60 max-w-full" />
        <h2 className="font-display tracking-tight mt-6 text-center text-3xl font-bold text-text-primary">
          {welcomeMode ? 'Welcome to SyncConnect' : 'Reset Your Password'}
        </h2>
        <p className="mt-2 text-center text-sm text-text-body">
          {status === 'invalid'
            ? (welcomeMode
                ? 'There was a problem with your setup link.'
                : 'There was a problem with your reset link.')
            : (welcomeMode
                ? 'Set your password to get started.'
                : 'Enter your new password below.')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-card py-8 px-4 shadow sm:rounded-xl sm:px-10">
          {status === 'verifying' && (
            <p className="text-center text-text-body">
              {welcomeMode ? 'Preparing your account...' : 'Verifying your reset link...'}
            </p>
          )}

          {status === 'invalid' && (
            <div className="space-y-4">
              <p className="text-status-danger text-center">{errorMsg}</p>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent-blue hover:bg-accent-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue"
              >
                {welcomeMode ? 'Request a new link' : 'Request a new reset link'}
              </button>
            </div>
          )}

          {(status === 'ready' || status === 'submitting' || status === 'success') && (
            <form className="space-y-6" onSubmit={handlePasswordReset}>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-text-body"
                >
                  New Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={status === 'submitting' || status === 'success'}
                    className="appearance-none block w-full px-3 py-2 border border-surface-border rounded-lg shadow-sm placeholder-text-muted focus:outline-none focus:ring-accent-blue focus:border-accent-blue sm:text-sm"
                  />
                </div>
              </div>

              {status === 'success' && (
                <p className="text-status-success">
                  Password updated successfully! Redirecting...
                </p>
              )}
              {errorMsg && status !== 'success' && (
                <p className="text-status-danger">{errorMsg}</p>
              )}

              <div>
                <button
                  type="submit"
                  disabled={status === 'submitting' || status === 'success'}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent-blue hover:bg-accent-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue"
                >
                  {status === 'submitting'
                    ? (welcomeMode ? 'Setting Password...' : 'Resetting...')
                    : (welcomeMode ? 'Set Password' : 'Reset Password')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
